import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authApi } from '../services/api';
import { Config } from '../config';
import { useLabels } from '../providers/LabelsProvider';
import { useT } from '../providers/I18nProvider';
import Toast from '../components/Toast';

export default function SignupPage() {
  const labels = useLabels();
  const t = useT();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invite');
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    title: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Redirect to login if no invitation token
  useEffect(() => {
    if (!invitationToken) {
      navigate('/login', { 
        state: { 
          message: 'Signup requires a valid invitation link. Please contact your administrator.' 
        } 
      });
    }
  }, [invitationToken, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Ensure invitation token is present
    if (!invitationToken) {
      setErrors({ general: 'Invalid invitation link. Please contact your administrator.' });
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setErrors({ confirmPassword: t('auth.passwordsDoNotMatch') });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Use authApi.signup which properly handles invitation_token
      await authApi.signup(
        {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: 'user'
        },
        {
          first_name: formData.first_name,
          last_name: formData.last_name,
          title: formData.title || ''
        },
        invitationToken  // Pass the invitation token from URL (required)
      );

      // Show success message before redirecting
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/login', { state: { message: 'Account created successfully! Please log in.' } });
      }, 2000);
    } catch (error) {
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'object' && detail.message) {
          setErrors({ password: `${detail.message}: ${detail.reasons?.join(', ')}` });
        } else if (typeof detail === 'string') {
          setErrors({ general: detail });
        }
      } else {
        setErrors({ general: 'Error creating account. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Don't render the form if no invitation token (will redirect)
  if (!invitationToken) {
    return null;
  }

  return (
    <div className="min-h-screen bg-eucnet-gradient flex items-center justify-center px-4 py-12">
      <Toast
        open={showSuccess}
        setOpen={setShowSuccess}
        type="success"
        className="fixed top-6 right-6 z-50"
      >
        {t('auth.accountCreatedRedirecting')}
      </Toast>
      
      <div className="max-w-2xl w-full space-y-8">
        <div>
          {/* EU CyberNet Logo */}
          <div className="flex justify-center overflow-hidden max-h-[140px] mb-0">
            <img
              src={labels.loginLogo || Config.loginLogo}
              alt="Logo"
              className="h-[196px] w-auto object-top"
            />
          </div>
          <h2 className="mt-2 text-center text-4xl font-bold text-white tracking-tight">
            {t('auth.createAccount')}
          </h2>
          <p className="mt-3 text-center text-base text-white/90 font-medium">
            Join the {labels.platformTitle} {labels.platformSubtitle}
          </p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-eucnet-navy mb-2">{t('auth.firstName')}</label>
                <input
                  type="text"
                  name="first_name"
                  required
                  className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                  placeholder={t('auth.firstName')}
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-eucnet-navy mb-2">{t('auth.lastName')}</label>
                <input
                  type="text"
                  name="last_name"
                  required
                  className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                  placeholder={t('auth.lastName')}
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-eucnet-navy mb-2">{t('auth.email')}</label>
              <input
                type="email"
                name="email"
                required
                className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                placeholder="your.email@organization.eu"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-eucnet-navy mb-2">{t('auth.username')}</label>
              <input
                type="text"
                name="username"
                required
                className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                placeholder="your.username"
                value={formData.username}
                onChange={handleChange}
              />
              {errors.username && <p className="mt-2 text-sm text-red-600">{errors.username}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-eucnet-navy mb-2">{t('auth.titleRole')} <span className="text-eucnet-grey-400 font-normal">({t('common.optional')})</span></label>
              <input
                type="text"
                name="title"
                className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                value={formData.title}
                onChange={handleChange}
                placeholder="Security Analyst, CISO, etc."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-eucnet-navy mb-2">{t('auth.password')}</label>
                <input
                  type="password"
                  name="password"
                  required
                  className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                />
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-eucnet-navy mb-2">{t('auth.confirmPassword')}</label>
                <input
                  type="password"
                  name="confirmPassword"
                  required
                  className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                {errors.confirmPassword && <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>
            </div>

            {errors.general && (
              <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-4 rounded-lg">
                {errors.general}
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-base font-semibold text-white bg-gradient-to-r from-eucnet-primary to-eucnet-sky hover:from-eucnet-navy hover:to-eucnet-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-eucnet-sky disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
              >
                {loading ? t('auth.creatingAccount') : t('auth.createAccountBtn')}
              </button>
            </div>

            <div className="text-center text-sm pt-2">
              <span className="text-eucnet-grey-600">{t('auth.hasAccount')}</span>{' '}
              <Link to="/login" className="font-semibold text-eucnet-blue hover:text-eucnet-sky transition-colors">
                {t('auth.signInLink')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
